@let alloc = store.allocation();
<div class="flex flex-col gap-y-15">
  <div class="flex flex-col gap-y-5">
    <p class="text-sm text-gray">Review the full request below before submitting.</p>
  </div>

  <!-- 🔴 Closing the account, stated first and in red.

       The only irreversible thing this wizard can file, and it is reached by ticking a checkbox
       on step 1 — easy to have set and then moved past. Everything else on this page reports what
       the request does; this reports what it ENDS, so it leads.

       Red, unlike the SMA notice below: an SMA is a strategy the advisor chose, while this is a
       terminal action, and the two should not read as the same weight of statement. -->
  @if (store.isCloseAccount()) {
    <section class="flex px-15 py-10 border rounded gap-x-8 border-red-lighter bg-red-lightest">
      <i class="material-icons text-red-dark" style="font-size: 1.8rem" aria-hidden="true">error_outline</i>
      <div class="flex flex-col gap-y-2">
        <span class="font-semibold text-red-dark">This request closes the account</span>
        <p class="text-sm text-gray-darkest">
          Every position is liquidated and the full account value is distributed. The account will
          be
          @if (store.fundingForm.controls.isCloseDateBilled.value) {
            <strong>billed on its close date</strong>.
          } @else {
            <strong>not billed on its close date</strong>.
          }
        </p>
      </div>
    </section>
  }

  <!-- What an SMA means, restated where the advisor signs off on it.

       Mint, not red: this is a statement about the strategy they chose, not a fault. It sits
       ABOVE the blockers panel so the two never argue about which is the headline, and it is
       unconditional on this page once committed -- reaching Review is the only thing a
       confirmed SMA can do.

       The confirmation question is deliberately absent. That was asked and answered on the
       allocation step; repeating it here would read as a second decision to make. -->
  @if (store.smaModelName(); as smaModel) {
    <section class="flex px-15 py-10 border rounded gap-x-8 border-mint-light bg-mint-lightest">
      <i class="material-icons text-mint-darkest" style="font-size: 1.8rem" aria-hidden="true">info</i>
      <div class="flex flex-col gap-y-2">
        <span class="font-semibold text-mint-darkest">{{ smaModel }} is a Separately Managed Account</span>
        <!-- ⚠️ Word for word with the ADD dialog on the allocation step, and that is the point:
             this panel restates what the advisor already agreed to, where they sign off on it.
             Two wordings for one rule invite the reader to hunt for the difference.

             The substantive edit is "or assets" -- it said "other models", which is narrower
             than the rule and is the thing an advisor most needs to be sure of here. -->
        <p class="text-sm text-gray-darkest">
          Separately Managed Accounts (SMAs) are 100% of the account and cannot be commingled with
          other models or assets. All buys and sells are executed by an external manager. This
          strategy requires a custodial Limited Power of Attorney (LPOA) on file for the client.
        </p>
      </div>
    </section>
  }

  <!-- What is still outstanding, stated where the advisor is deciding whether the request is
       finished rather than beside a disabled Next three steps back.
       
       These are REQUEST-level conditions, not step ones -- money left unplaced can be resolved
       by selling more, buying, protecting cash, or allocating, and which of those the advisor
       wants is not something the allocation step gets to assume. So reaching this page is
       free, and it is Submit that stays shut. -->
  @if (store.submitBlockers().length) {
    <section class="flex flex-col px-15 py-10 border rounded gap-y-5 border-red-lighter bg-red-lightest">
      <div class="flex items-center font-semibold gap-x-8 text-red-dark">
        <i class="material-icons" style="font-size: 1.8rem" aria-hidden="true">warning</i>
        <span>This request can't be submitted yet</span>
      </div>
      <ul class="flex flex-col ml-30 text-sm list-disc gap-y-2 text-gray-darkest">
        @for (blocker of store.submitBlockers(); track blocker) {
          <li>{{ blocker }}</li>
        }
      </ul>
    </section>
  }

  <!-- ⚠️ MERGE: two reorderings of the same page, and they agree once stated together.
       Develop moved Model Allocation to the TOP because an advisor reviewing their own work
       looks for the decision they made, not the trades that carry it out. The move-money types
       state no target allocation at all -- their decision is the funding figure -- so Funding
       leads for them and Model Allocation is withheld entirely.

       Same rule, two request families: whatever the advisor actually decided goes first. -->
  <!-- ============ Funding (Contribution / Distribution) ============
       The money and how it travels, as a definition list rather than a table: these are single
       values, not rows, and a two-column table of one-value rows is a table pretending. -->
  @if (store.isMoveMoney()) {
    <section class="border rounded border-gray-lighter">
      <header class="px-15 py-8 text-sm font-bold tracking-wide uppercase border-b bg-gray-lightest text-gray-darkest border-gray-lighter">
        {{ fundingHeader() }}
      </header>
      <dl class="grid grid-cols-1 px-15 py-10 text-sm md:grid-cols-2 gap-x-30 gap-y-8">
        <div class="flex justify-between gap-x-10">
          <dt class="text-gray">{{ amountLabel() }}</dt>
          <dd class="font-semibold text-gray-darkest" style="font-variant-numeric: tabular-nums">
            {{ store.fundingAmount() | currency }}
          </dd>
        </div>

        @if (store.isDistribution()) {
          <div class="flex justify-between gap-x-10">
            <dt class="text-gray">Tax withholding</dt>
            <dd class="text-gray-darkest">{{ withholdingLabel() }}</dd>
          </div>
          <!-- The figure the CLIENT sees, which is not the figure the trade team acts on. Both
               are shown because the advisor is asked for both by clients, and only one of them
               leaves the account. -->
          <div class="flex justify-between gap-x-10">
            <dt class="text-gray">Client receives</dt>
            <dd class="font-semibold text-mint-darker" style="font-variant-numeric: tabular-nums">
              {{ store.netAmount() | currency }}
            </dd>
          </div>
        }

        <div class="flex justify-between gap-x-10">
          <dt class="text-gray">Method</dt>
          <dd class="text-gray-darkest">{{ store.selectedDeliveryMethod()?.name ?? '—' }}</dd>
        </div>
        <!-- WHERE IT LANDS -- a bank account, or the address a cheque is posted to. One row for
             both, because only one of the two is ever asked and they answer the same question.
             The label follows: an advisor reading this should see the word they picked under.

             🔴 ORANGE WHEN NOTHING IS ON FILE. Every other line in this grid is a neutral
             setting; this is the one saying a client's money is going somewhere the custodian has
             no authorization or address for, and it is the line a reviewer most needs to catch.
             The Payee block below, or the attached instructions, is what makes that filable. -->
        <div class="flex justify-between gap-x-10">
          <dt class="text-gray">{{ destinationLabel() }}</dt>
          <dd [class]="store.isNewRecipient()
            ? 'font-semibold text-orange-darker' : 'text-gray-darkest'">
            {{ bankLabel() }}
          </dd>
        </div>
        <div class="flex justify-between gap-x-10">
          <dt class="text-gray">Frequency</dt>
          <dd class="text-gray-darkest">{{ scheduleLabel() }}</dd>
        </div>
        @if (store.allowsOvernightDelivery()) {
          <div class="flex justify-between gap-x-10">
            <dt class="text-gray">Overnight delivery</dt>
            <dd class="text-gray-darkest">
              {{ store.fundingForm.controls.isOvernightDelivery.value ? 'Yes' : 'No' }}
            </dd>
          </div>
        }
        <!-- ⚠️ Surfaced because the trade team acts on it: an instruction with no advisory
             authority on file needs custodial paperwork before the money can move.
             🔴 BANK METHODS ONLY, and null is not false -- both tests live in `noIaAuthority`,
             which the funding step's panel reads as well. This row asked the chosen row directly
             and so printed 'None on file for this selection' on a CHEQUE, which has no bank
             authorization to lack one. -->
        @if (store.noIaAuthority()) {
          <div class="flex justify-between gap-x-10">
            <dt class="text-gray">Standing authorization</dt>
            <dd class="font-semibold text-orange-darker">None on file for this selection</dd>
          </div>
        }
      </dl>

      <!-- 🔴 A NOTICE, NOT A ROW IN THE LIST ABOVE, and it was a row first (AndyP, 2026-09-24).
           Every other line in that grid is a SETTING the advisor chose -- the amount, the method,
           the bank account. This is a CONSEQUENCE of the arithmetic: the request sells more than
           it pays out, and the difference stays behind. Sat among the settings in the same grey it
           read as one more field nobody needed to check, which is exactly the opposite of why it
           is here.

           ⚠️ MINT, NOT ORANGE. Nothing is wrong -- shares are granular, so covering the last few
           dollars means selling one more and overshooting, which is the design. Orange would ask
           the advisor to fix something that is working as intended, and the page already spends
           orange on the missing standing authorization a few lines up.

           🟢 Only when there IS a surplus. An exactly-met request says nothing, rather than
           printing "$0.00 stays in the account" and inviting the reader to wonder why. -->
      <!-- WHERE THE CHECK IS POSTED. A block rather than six rows in the grid above: those are
           single settings, this is one address that has to be read as a whole to be checked.
           Mint rather than orange -- nothing is wrong once it is filled in, and the page already
           spends orange on the Sent to line and on anything outstanding. -->
      @if (store.payeeRequired() && !store.payeeIncomplete()) {
        @let payee = store.fundingForm.controls.payee.value;
        <div class="flex flex-col px-15 py-10 mt-15 border rounded gap-y-2 border-gray-lighter bg-gray-lightest">
          <!-- 🔴 WHOSE ADDRESS IT IS, IN THE HEADING, because it changes what the three lines
               below mean. A cheque to the client at an address Schwab has never seen and a
               cheque to a stranger are the same address block and completely different things
               to approve -- and this page is the last screen before the money is committed.

               Beside 'Payee' rather than on a line of its own: it qualifies the block, it is not
               another field in it. Both trackers print it the same way, leading, for the same
               reason.

               ⚠️ The LABEL, never isThirdParty. The lookup is editable from the CRM Lookups
               screen, so a relabel reaches the screen and keys on nothing. -->
          <span class="text-xs font-semibold tracking-wide uppercase text-gray">
            Payee
            @if (store.selectedRecipientType(); as recipient) {
              <span [class.text-orange-dark]="recipient.isThirdParty">
                &mdash; {{ recipient.name }}
              </span>
            }
          </span>
          <span class="text-sm font-semibold text-gray-darkest">{{ payee.payeeName }}</span>
          <span class="text-sm text-gray-dark">{{ payee.streetAddress }}</span>
          @if (payee.addressContinued) {
            <span class="text-sm text-gray-dark">{{ payee.addressContinued }}</span>
          }
          <span class="text-sm text-gray-dark">
            {{ payee.city }}, {{ payee.state }} {{ payee.zip }}
          </span>
        </div>
      }

      <!-- ⚠️ NO OUTSTANDING-DOCUMENT PANEL HERE, and its absence is deliberate. It lived here
           briefly, worded as a statement beside the blocker panel's imperative -- two sentences
           saying one thing on one screen, which reads as two problems and sends the advisor
           looking for a second thing to fix. `submitBlockers` carries it now, which is where
           every other reason Submit is shut already appears (d644c37e). -->

      @if (store.isDistribution() && store.unappliedValue() < 0) {
        <div class="flex px-15 py-10 mt-15 border rounded gap-x-8 border-mint-light bg-mint-lightest">
          <i class="material-icons text-mint-darkest" style="font-size: 1.8rem" aria-hidden="true">info</i>
          <div class="flex flex-col gap-y-2">
            <!-- The sentence below already says where it goes; the headline names the figure. -->
            <span class="font-semibold text-mint-darkest">
              {{ -store.unappliedValue() | currency }} surplus
            </span>
            <span class="text-sm text-gray-dark">
              This request raises more funds than will be paid out. The surplus will be left in
              the account as cash.
            </span>
          </div>
        </div>
      }
    </section>
  }

  <!-- ============ Sleeve amounts ============
       Only when the request actually apportions across sleeves. A close account does not (the
       whole account is going), and an account with no model sleeves has nothing to show. -->
  @if (store.isMoveMoney() && !store.isCloseAccount() && store.sleeveAmounts().rows.length) {
    <section class="border rounded border-gray-lighter">
      <header class="px-15 py-8 text-sm font-bold tracking-wide uppercase border-b bg-gray-lightest text-gray-darkest border-gray-lighter">
        {{ sleeveHeader() }}
      </header>
      <div class="w-full overflow-x-auto">
        <table class="w-full text-sm border-collapse">
          <thead>
            <tr class="text-xs uppercase text-gray">
              <th class="p-8 text-left">Model</th>
              <th class="p-8 text-right">Current Value</th>
              <th class="p-8 text-right">{{ store.isContribution() ? 'Amount In' : 'Amount Out' }}</th>
            </tr>
          </thead>
          <tbody>
            <!-- Every sleeve, including the ones getting nothing. A sleeve left at zero IS the
                 instruction -- "do not touch this one" -- and dropping those rows would leave
                 the trade team unable to tell an untouched sleeve from one nobody considered. -->
            @for (row of store.sleeveAmounts().rows; track row.sleeveKey) {
              <tr class="border-t border-gray-lighter">
                <td class="p-8 font-semibold text-gray-darkest">{{ row.modelName }}</td>
                <td class="p-8 text-right text-gray-dark" style="font-variant-numeric: tabular-nums">
                  {{ row.currentValue | currency }}
                </td>
                <td class="p-8 text-right text-gray-darkest" style="font-variant-numeric: tabular-nums">
                  {{ row.amount | currency }}
                </td>
              </tr>
            }
          </tbody>
          <tfoot>
            <tr class="font-semibold text-gray-darkest" style="background-color: #fafafa">
              <td class="p-8 border-t border-gray-lighter" colspan="2">Total</td>
              <td class="p-8 text-right border-t border-gray-lighter" style="font-variant-numeric: tabular-nums">
                {{ store.sleeveAmounts().total | currency }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </section>
  }

  <!-- ============ Custodial cash taken (distribution) ============
       Its own line rather than a row in the sells table: cash is not sold, and putting it there
       would give it a share count and a price it does not have. -->
  @if (store.isDistribution() && store.cashTaken() > 0) {
    <section class="border rounded border-gray-lighter">
      <header class="px-15 py-8 text-sm font-bold tracking-wide uppercase border-b bg-gray-lightest text-gray-darkest border-gray-lighter">
        Custodial Cash
      </header>
      <div class="flex justify-between px-15 py-10 text-sm gap-x-10">
        <span class="text-gray">Taken from the Custom Holdings sleeve</span>
        <span class="font-semibold text-gray-darkest" style="font-variant-numeric: tabular-nums">
          {{ store.cashTaken() | currency }}
        </span>
      </div>
    </section>
  }

  <!-- ⚠️ Withheld from the move-money types, and not merely because their grid is empty. A
       contribution or distribution does not change the account's target allocation at all: it
       moves dollars against the sleeves that already exist, which is what the Funding section
       above reports. Rendering this would show a grid of zeros implying they had set every
       target to nothing. -->
  @if (!store.isMoveMoney()) {
    <!-- Model allocation — FIRST on the page.

         ⚠️ The section order here used to be sells, buys, custom holdings, models: trades grouped
         together with the models left to the end. That reads fine as a document and badly as a
         review — an advisor checking their own work is looking for the decisions they made, and
         the models are the decision. The trades are how it gets there.

         So the page now opens with where the money is going, then the three sections that move
         it: sold, bought, and what stays put.

         🟢 Purely presentational. No section computes from another, so they can be ordered for
         reading. -->
    <section class="border rounded border-gray-lighter">
      <header class="px-15 py-8 text-sm font-bold tracking-wide uppercase border-b bg-gray-lightest text-gray-darkest border-gray-lighter">
        <!-- "Account Allocation", not "Model Allocation" (AndyP, 2026-09-15). The table stopped
             being about models the moment the Custom Holdings row joined it and the Total became
             the whole account -- a header naming only half the rows would have the reader treating
             the sleeve line as something that wandered in. -->
        Account Allocation
      </header>
      <table class="w-full text-sm border-collapse table-fixed">
        <colgroup>
          <col style="width: 36%" />
          <col style="width: 16%" />
          <col style="width: 16%" />
          <col style="width: 16%" />
          <col style="width: 16%" />
        </colgroup>
        <thead>
          <tr class="text-gray-dark">
            <th class="px-15 py-5 text-left">Model</th>
            <th class="px-15 py-5 text-right">Target %</th>
            <th class="px-15 py-5 text-right">Distribution %</th>
            <th class="px-15 py-5 text-right">Contribution %</th>
            <th class="px-15 py-5 text-right">Allocated Value</th>
          </tr>
        </thead>
        <tbody>
          @for (row of alloc.rows; track row.id) {
            @if (row.modelId) {
              <tr class="border-t border-gray-lightest">
                <td class="px-15 py-8 font-medium">
                  <span class="flex items-center gap-x-5 min-w-0">
                    <span class="truncate">{{ store.modelName(row.modelId) }}</span>
                    <!-- ⚠️ NEW means the SLEEVE does not exist in Orion yet, not that the model is
                         unfamiliar. `orionConnectAccountId == null` is the established discriminator
                         for "this row was added rather than loaded" -- the same test `modelSetChanged`
                         and `buildRequest`'s `isNew` use, so the badge cannot disagree with what is
                         filed.

                         ⚠️ NOT suppressed when every row is new. Marking all of them does distinguish
                         nothing between rows -- but REMOVED badges unconditionally just below, so
                         suppressing this one would leave the table with two vocabularies for the same
                         kind of fact. A badge means something changed about that sleeve; no badge
                         means it carries forward. CRM's copy of this table works the same way. -->
                    @if (store.isNewSleeveRow(row)) {
                      <span class="px-8 py-1 border rounded-full flex-shrink-0 text-mint-darkest bg-mint-lightest border-mint-light"
                        style="font-size: 1rem"
                        title="This sleeve does not exist in Orion yet and will be created">New</span>
                    }
                  </span>
                  <!-- WHAT THIS SLEEVE HELD, AND WHAT IT GAVE UP. Cash raised is always money leaving
                       the model, so it is always red.

                       🔴 RE-WEIGHTED IS DELIBERATELY NOT HERE (AndyP, 2026-10-29). It is the
                       sleeve coming back to its own stated target -- real, and really traded, but
                       not something the advisor asked for or can act on: on an untouched request
                       it appears unprompted, because a drifted account restates itself the moment
                       it loads. Fodder on the screen where somebody is checking their own
                       instruction. The TRADE TEAM keeps it in the CRM tracker, where the
                       difference between a cash raise and a rebalance IS the work.

                       Absent on an added row: there was no sleeve to hold anything, the New badge
                       says so, and Allocated Value already states what it is being given. -->
                  @if (rowCurrent(row); as current) {
                    @let cashRaised = rowCashRaised(row);
                    <span class="block mt-1 text-xs font-normal text-gray"
                      style="font-variant-numeric: tabular-nums">
                      Current Value {{ money(current) }}
                      @if (moved(cashRaised)) {
                        · Cash Raised <span class="text-red-dark">{{ money(cashRaised) }}</span>
                      }
                    </span>
                  }
                </td>
                <td class="px-15 py-8 text-right">{{ row.effectiveTarget | number: '1.2-2' }}%</td>
                <td class="px-15 py-8 text-right">{{ row.effectiveDistribution | number: '1.2-2' }}%</td>
                <td class="px-15 py-8 text-right">{{ row.effectiveContribution | number: '1.2-2' }}%</td>
                <td class="px-15 py-8 text-right">{{ row.allocatedValue | currency }}</td>
              </tr>
            }
          }
          <!-- SLEEVES BEING CLOSED, in the same table as the ones being kept.

               ⚠️ A closure the reader has to infer from an absence is one they will miss. Every
               other instruction in this request has a row; without this, "stop using this model"
               is the only one carried by a row NOT being there.

               ⚠️ No percentages and no value, and NOT in the Total below -- that figure is the
               requested allocation, and this sleeve is not part of it. Dashes rather than zeroes,
               because 0.00% would read as "allocate nothing to it", which is a different
               instruction from "close it".

               Red, matching the sell rows above: both mark something leaving the account. -->
          @for (s of store.removedSleeves(); track s.accountId) {
            <tr class="border-t border-gray-lightest">
              <td class="px-15 py-8">
                <span class="flex items-center gap-x-5 min-w-0">
                  <span class="truncate text-gray">{{ s.modelAggName || 'Unknown Model' }}</span>
                  <span class="px-8 py-1 border rounded-full flex-shrink-0 text-red-dark bg-red-lightest border-red-light"
                    style="font-size: 1rem"
                    title="The account holds this sleeve today; this request does not carry it forward">Removed</span>
                </span>
                <!-- The closure takes whatever is left; what Raise Cash had already cashRaised is
                     stated separately so the two are not read as one instruction. -->
                @let removedCashRaisedValue = removedCashRaised(s);
                <span class="block mt-1 text-xs font-normal text-gray"
                  style="font-variant-numeric: tabular-nums">
                  Current Value {{ money(s.currentValue) }}
                  @if (moved(removedCashRaisedValue)) {
                    · Cash Raised <span class="text-red-dark">{{ money(removedCashRaisedValue) }}</span>
                  }
                </span>
              </td>
              <td class="px-15 py-8 text-right text-gray-light">&#8212;</td>
              <td class="px-15 py-8 text-right text-gray-light">&#8212;</td>
              <td class="px-15 py-8 text-right text-gray-light">&#8212;</td>
              <td class="px-15 py-8 text-right text-gray-light">&#8212;</td>
            </tr>
          }
          <!-- THE NON-MODEL HALF OF THE ACCOUNT, so this table can total to the whole thing.

               ⚠️ THE ONLY PLACE THE FULL ACCOUNT IS ADDED UP on this page, which is why it earns a
               row in somebody else's table rather than a section of its own. Every other section
               totals one instruction -- what is sold, what is bought, what is held -- and the
               advisor was left adding those up by hand to check the request still describes their
               whole account. It does: models plus this sleeve IS the account, once nothing is left
               unallocated.

               ⚠️ `customHoldingsValue`, not the bar's `customHoldingsExcludingCashOnHold`. The bar
               splits cash-on-hold into its own run so money MOVING between them is visible while
               the advisor works; this is a static statement of what the sleeve is worth, and the
               held cash is inside it. Using the bar's narrower figure here would make the account
               total come up short by exactly the pending holds.

               ⚠️ Percentages are blank, not 0.00%. A zero would read as an instruction to target
               nothing, which is what a closed sleeve says -- see the Removed rows above. This
               sleeve has no target: it holds what the models do not.

               Mint and a left rule, the same device the CASH band uses further down, so the row
               reads as part of the request but plainly not as a model. -->
          @if (store.customHoldingsValue() > 0) {
            <tr class="border-t border-gray-lightest bg-mint-lightest">
              <td class="px-15 py-8 font-semibold border-l-4 text-mint-darkest border-mint-light">Custom Holdings</td>
              <td class="px-15 py-8 text-right text-gray-light">&#8212;</td>
              <td class="px-15 py-8 text-right text-gray-light">&#8212;</td>
              <td class="px-15 py-8 text-right text-gray-light">&#8212;</td>
              <td class="px-15 py-8 font-semibold text-right text-mint-darkest">{{ store.customHoldingsValue() | currency }}</td>
            </tr>
          }
        </tbody>
        <tfoot>
          <tr class="border-t border-gray-lighter font-semibold text-gray-darkest" style="background-color: #fafafa">
            <!-- ⚠️ The ONE aggregate worth stating, and the reason the per-row figures can
                 live in a sub-line: what the request raised out of the models is the figure a
                 reader checks, and summing sub-lines by eye is exactly what they should not
                 have to do. Includes sleeves being closed, which have rows above but are
                 deliberately outside the Total beside it. -->
            <td class="px-15 py-4" colspan="4">
              Total
              @if (moved(cashRaisedTotal())) {
                <span class="ml-5 text-xs font-normal text-gray">
                  · <span class="text-red-dark">{{ money(cashRaisedTotal()) }}</span> raised from models
                </span>
              }
            </td>
            <!-- Sums the rows actually shown, which now include the sleeve. It was the requested
                 MODEL allocation alone while that was all the table listed; a total that left a
                 visible row out would be the same fault the buys table had. -->
            <td class="px-15 py-4 text-right">{{ alloc.totals.value + store.customHoldingsValue() | currency }}</td>
          </tr>
        </tfoot>
      </table>
    </section>
  }

  <!-- Positions being sold. Hidden entirely when there are none, exactly as the bought
       section is: a reallocation that only changes the model mix sells nothing, and an empty
       table with a $0.00 Total read as though something had gone wrong rather than as though
       nothing was asked for. -->
  @if (store.soldPositions().length) {
    <section class="border rounded border-gray-lighter">
      <header class="px-15 py-8 text-sm font-bold tracking-wide uppercase border-b bg-gray-lightest text-gray-darkest border-gray-lighter">
        Positions Being Sold
      </header>
      <table class="w-full text-sm border-collapse table-fixed">
        <!-- Six columns since the ACTION column went. Shares to Sell takes the widest share of
             what it cashRaised, because it now carries the Sell All pill beside its figure. -->
        <colgroup>
          <col style="width: 11%" />
          <col style="width: 26%" />
          <col style="width: 22%" />
          <col style="width: 13%" />
          <col style="width: 13%" />
          <col style="width: 15%" />
        </colgroup>
        <thead>
          <!-- ⚠️ No ACTION column. It used to lead, matching the CRM request tracker's own,
               on the reasoning that the advisor and the trade team should read the same words
               for the same thing. It cost a whole column to say something the Shares figure
               already implies, so the label moved next to that figure instead and the column
               went.

               The CRM tracker keeps its ACTION column: it lists sells and buys in ONE table,
               so there the column is what tells them apart. This table is sells only. -->
          <tr class="text-gray-dark">
            <th class="px-15 py-5 text-left">Ticker</th>
            <th class="px-15 py-5 text-left">Description</th>
            <th class="px-15 py-5 text-right">Shares to Sell</th>
            <th class="px-15 py-5 text-right">Shares Kept</th>
            <th class="px-15 py-5 text-right">Price</th>
            <th class="px-15 py-5 text-right">Market Value</th>
          </tr>
        </thead>
        <tbody>
          @for (p of store.soldPositions(); track p.ticker) {
            <tr class="border-t border-gray-lightest">
              <td class="px-15 py-8 font-semibold">{{ p.ticker }}</td>
              <td class="px-15 py-8 text-gray-dark">
                <span class="flex flex-wrap items-center gap-x-5 gap-y-2">
                  <span>{{ p.description }}</span>
                  <!-- ⚠️ REQUIRES REVIEW, and it belongs on the sell rather than only in the cash
                       band. The band says how MUCH is held back; this says WHICH sales did it,
                       and that is the part the trade team acts on.

                       ⚠️ BOTH SIDES NOW READ "Review Required", and the half after the em dash
                       is what separates them: a sell's funds are not immediately available, where
                       a buy's are held in cash until it can be purchased. The flag used to carry
                       the whole distinction in one word each -- "Requires review" against
                       "Pending review" -- which asked the reader to remember which was which.
                       Saying the consequence outright costs a few characters and needs no key.

                       ⚠️ "ALPHASTAR Review Required" as of 2026-09-16, on pilot feedback: the
                       advisor needs to know WHO is reviewing, not merely that somebody is. This
                       badge has now been "Pending review", "Requires review", "Review Required"
                       and this — change it in one sweep across both surfaces or they diverge.

                       ⚠️ "Review Required", not "Requires Review" (AndyP, 2026-09-15). A badge is
                       a STATUS LABEL, not a sentence about the row -- every sibling here reads
                       that way already (Sell All, Do Not Trade, New, Removed), and this was the
                       one third-person verb phrase among them. It also puts the operative word
                       first, which is what gets scanned in a narrow column, and leaves the dash
                       separating a state from its consequence rather than a verb phrase from a
                       fragment.

                       CRM's own pills still read the short form. They are on a screen the trade
                       team reads, not the advisor, and its columns are narrower.

                       Amber, not red: the sale is going through. Nothing is blocked. -->
                  @if (p.requiresReview) {
                    <span class="px-8 py-1 border rounded-full flex-shrink-0 text-orange-darker bg-orange-lightest border-orange-lighter"
                      style="font-size: 1rem"
                      title="Proceeds are held as cash and set aside for future allocation — they cannot be used in this request">Alphastar Review Required — Funds not immediately available</span>
                  }
                </span>
              </td>
              <!-- The Sell All pill sits with the figure it qualifies, and ONLY on a full sell.
                   A partial sell carries no pill: the previous column labelled every row, which
                   made the common case (checking a position seeds the full lot, so a full sell is
                   the default) shout as loudly as the exception.

                   ⚠️ Pill BEFORE the number, not after, so the figures stay flush to the right
                   edge and the column can still be read down. Trailing it would push the number
                   left by the pill's width on some rows and not others.

                   ⚠️ The figure carries a min-width, and that is what lines the PILLS up with
                   each other. The group is right-aligned, so without it every pill's left edge
                   moved with the width of the number beside it -- 810 and 1,260 pushed their
                   pills to visibly different places and the column read as ragged. Reserving
                   the figure's width fixes the pill's offset from the right edge instead, and
                   the digits stay right-aligned inside it.

                   A figure wider than the reserve still expands it and shifts that row's pill,
                   which is the honest trade: 4.5rem covers the share counts this table sees,
                   and a seven-figure lot is worth a ragged row rather than a permanently wide
                   column.

                   Shares keeps the action's colour -- red for a full sell, amber for a trim,
                   which is now the only marker a partial carries. The quantity IS the difference
                   between the two instructions. Market Value stays neutral: it is money out
                   either way, and it is what the red Total below sums. -->
              <td class="px-15 py-8 text-right"
                [class.text-red-dark]="p.isSellAll" [class.text-orange-darker]="!p.isSellAll">
                <span class="inline-flex items-center gap-x-8">
                  @if (p.isSellAll) {
                    <span class="px-8 py-1 font-semibold border rounded-full text-red-dark bg-red-lightest border-red-lighter"
                      style="font-size: 1rem" title="Every share of this position is being sold">Sell All</span>
                  }
                  <span class="inline-block text-right" style="min-width: 4.5rem">
                    {{ p.sharesToSell | number }}
                  </span>
                </span>
              </td>
              <td class="px-15 py-8 text-right">{{ p.isPartialSell ? (p.keptShares | number) : '—' }}</td>
              <td class="px-15 py-8 text-right">{{ p.price != null ? (p.price | currency) : '—' }}</td>
              <td class="px-15 py-8 text-right">{{ p.soldValue | currency }}</td>
            </tr>
          } @empty {
            <tr><td colspan="6" class="italic text-center px-15 py-10 text-gray">No positions being sold.</td></tr>
          }
        </tbody>
        <tfoot>
          <tr class="border-t border-gray-lighter font-semibold text-gray-darkest" style="background-color: #fafafa">
            <td class="px-15 py-4" colspan="5">Total</td>
            <td class="px-15 py-4 text-right">{{ store.totalSoldValue() | currency }}</td>
          </tr>
        </tfoot>
      </table>
    </section>
  }

  <!-- Positions being bought. Hidden entirely when there are none, so the step reads the
       same as before for a sell-only reallocation.

       ⚠️ EVERY actionable buy, pending ones included, as of 2026-09-15. It listed resolved buys
       alone for four days on the reasoning that an unidentified ticker is not something the trade
       team can buy yet -- true, and it cost more than it bought: a request whose only buy was a
       custom CUSIP showed NO buys table at all, so the one thing the advisor had asked for was
       invisible on the page that reports what they asked for.

       What they requested and what can be executed today are different questions. This section
       answers the first; the Review Required flag answers the second on the row itself, and the
       CASH ON HOLD band below carries the money. -->
  @if (store.actionableBuys().length) {
    <section class="border rounded border-gray-lighter">
      <header class="px-15 py-8 text-sm font-bold tracking-wide uppercase border-b bg-gray-lightest text-gray-darkest border-gray-lighter">
        Positions Being Bought
      </header>
      <table class="w-full text-sm border-collapse table-fixed">
        <colgroup>
          <col style="width: 11%" />
          <col style="width: 29%" />
          <col style="width: 15%" />
          <col style="width: 15%" />
          <col style="width: 15%" />
          <col style="width: 15%" />
        </colgroup>
        <thead>
          <tr class="text-gray-dark">
            <th class="px-15 py-5 text-left">Ticker</th>
            <th class="px-15 py-5 text-left">Description</th>
            <th class="px-15 py-5 text-right">Shares to Buy</th>
            <th class="px-15 py-5 text-right">Price / Share</th>
            <th class="px-15 py-5 text-right">Priced</th>
            <th class="px-15 py-5 text-right">Est. Amount</th>
          </tr>
        </thead>
        <tbody>
          @for (b of store.actionableBuys(); track b.id) {
            <tr class="border-t border-gray-lightest">
              <td class="px-15 py-8 font-semibold">{{ b.security?.ticker }}</td>
              <!--
                The badge sits in Description because a hand-typed ticker never has one —
                the cell is empty on exactly the rows that need flagging, so the label
                fills a gap instead of competing with the ticker for space. It is still
                on the row's own line, which is what matters for scanning.
              -->
              <td class="px-15 py-8 text-gray-dark">
                @if (b.needsTradeTeamReview) {
                  <!-- ⚠️ SAME PILL as the sells table and the Pending review flag below it, rather
                       than the uppercase, letter-spaced, square-cornered one this used to carry.
                       Two flags saying the same words in two shapes read as two different states,
                       which is the opposite of what a flag is for -- and on this page they sit a
                       few rows apart. The rounded-full, sentence-case, 1rem form is the family
                       every other pill in this wizard already belongs to, including Sell All. -->
                  <span class="px-8 py-1 border rounded-full flex-shrink-0 text-orange-darker bg-orange-lightest border-orange-lighter"
                    style="font-size: 1rem"
                    title="Entered manually — not matched to a security on file, so the amount is held in cash on the account until the Trade Team resolves it">Alphastar Review Required — Funds held in cash until purchased</span>
                } @else {
                  {{ b.security?.description }}
                }
              </td>
              <!-- Em dash, never 0: a hand-typed row is entered as an amount, so it has no
                   share count at all. A zero would read as an order for no shares. -->
              <td class="px-15 py-8 text-right text-mint-darkest">
                {{ b.needsTradeTeamReview ? '—' : (b.shares | number) }}
              </td>
              <td class="px-15 py-8 text-right">
                {{ b.security?.price != null ? (b.security?.price | currency) : '—' }}
              </td>
              <td class="px-15 py-8 text-right text-gray-dark">
                {{ b.security?.priceAsOf ? (b.security?.priceAsOf | date: 'shortDate') : '—' }}
              </td>
              <!-- Shown on BOTH kinds of row. On a priced row this is shares x price; on a
                   hand-typed one it is the figure the advisor entered, which is the only
                   thing that row says about how much to buy. Hiding it here left the
                   estimated total unexplainable - it counts the amount, so the row has to
                   show it. -->
              <td class="px-15 py-8 text-right">
                {{ b.estimatedAmount | currency }}
              </td>
            </tr>
          }
        </tbody>
        <tfoot>
          <tr class="border-t border-gray-lighter font-semibold text-gray-darkest" style="background-color: #fafafa">
            <td class="px-15 py-4" colspan="5">Estimated Total</td>
            <!-- ⚠️ `totalBuyValue`, which counts the pending rows, because the table now SHOWS
                 them with their amounts. A total that silently omits visible rows reads as
                 complete and is not, and this is the screen the advisor returns to in order to
                 confirm what they filed. It was `resolvedBuyValue` while those rows were absent,
                 which was right then and is wrong now. -->
            <td class="px-15 py-4 text-right">{{ store.totalBuyValue() | currency }}</td>
          </tr>
        </tfoot>
      </table>
      <p class="px-15 py-8 text-xs border-t text-gray border-gray-lighter">
        Buys execute on the <strong>share count</strong>. Amounts are estimates from the last reported price,
        so the final value will differ.
        <!-- ⚠️ This note has now reversed TWICE with the table, so read it against the total
             above before editing either. The pending amounts are back IN that total, because
             they are back in the table; what the note has to do is explain why the same money
             also appears further down, rather than let it read as counted twice by mistake. -->
        @if (store.buysNeedingReview().length) {
          <span class="block mt-2">
            A ticker that is not a security on file cannot be bought until the Trade Team resolves
            it. Those amounts are included above as <strong>requested</strong>, and the matching
            cash hold is shown under <strong>Pending</strong> below.
          </span>
        }
      </p>
    </section>
  }

  <!-- Custom Holdings sleeve.

       Hidden outright once an SMA is committed rather than rendered empty. The manager takes
       the whole account and liquidates it, so `sleevePositions` is already empty by then and
       this would show a table of nothing totalling $0.00 — which reads as a fault rather than
       as the point. The notice at the top of the page is what accounts for the positions.

       ⚠️ Hidden for a DISTRIBUTION for a different reason: that request writes no
       `req.CustomHolding` rows at all (the sells and the cash taken are the whole instruction),
       so a "resulting sleeve" table here would assert a restructure the request does not carry.
       A contribution DOES write them — current holdings, plus buys, plus the CASH line — so it
       keeps this section. -->
  @if (!store.smaModelName() && !store.isDistribution()) {
    <section class="border rounded border-gray-lighter">
      <header class="px-15 py-8 text-sm font-bold tracking-wide uppercase border-b bg-gray-lightest text-gray-darkest border-gray-lighter">
        Custom Holdings
      </header>
      <table class="w-full text-sm border-collapse table-fixed">
        <colgroup>
          <col style="width: 11%" />
          <col [style.width]="showDoNotTrade ? '33%' : '49%'" />
          @if (showDoNotTrade) { <col style="width: 16%" /> }
          <col style="width: 14%" />
          <col style="width: 13%" />
          <col style="width: 13%" />
        </colgroup>
        <thead>
          <tr class="text-gray-dark">
            <th class="px-15 py-5 text-left">Ticker</th>
            <th class="px-15 py-5 text-left">Description</th>
            @if (showDoNotTrade) {
              <th class="px-15 py-5 text-right">Do Not Trade</th>
            }
            <th class="px-15 py-5 text-right">Shares</th>
            <th class="px-15 py-5 text-right">Price</th>
            <th class="px-15 py-5 text-right">Est. Market Value</th>
          </tr>
        </thead>
        <tbody>
          <!-- The MERGED list: what the sleeve will hold, kept and bought combined, so a
               security that is both appears once — matching the single Custom Holdings row
               CRM will write for it rather than showing two lines for one holding. -->
          @for (p of store.resolvedHoldings(); track p.ticker) {
            <tr class="border-t border-gray-lightest">
              <!-- Ticker stays bare. The badges used to sit here and wrapped under it, because
                   this column is sized for a symbol - and a hand-typed row carries no
                   description, so it stacked three lines against an empty cell beside it.
                   Description has the room, and the buys table above already flags a
                   needs-review row in that same column. -->
              <td class="px-15 py-8 font-semibold">{{ p.ticker }}</td>
              <td class="px-15 py-8 text-gray-dark">
                <div class="flex flex-wrap items-center gap-x-5 gap-y-2">
                  @if (p.isNewPosition) {
                    <span class="px-8 py-1 font-normal border rounded-full text-mint-darkest bg-mint-lighter border-mint-light"
                      style="font-size: 1rem">New</span>
                  } @else if (p.boughtShares > 0) {
                    <span class="px-8 py-1 font-normal border rounded-full text-mint-darkest bg-mint-lighter border-mint-light"
                      style="font-size: 1rem">+{{ p.boughtShares | number }}</span>
                  }
                  <!-- A hand-typed buy landed here, so the VALUE is the advisor's figure and the
                       share count is still unknown - which is why Shares and Price read as em
                       dashes on this row rather than as zeroes. -->
                  <!-- ⚠️ Swept to the shared phrase on 2026-09-16. It was the last "Pending review"
                       left in the portal, and leaving it would have put two names for one state on
                       adjacent sections of the same page. -->
                  @if (p.needsReview) {
                    <span class="px-8 py-1 font-normal border rounded-full text-orange-darker bg-orange-lightest border-orange-lighter"
                      style="font-size: 1rem"
                      title="Entered as an amount against a ticker that is not a security on file — the trade team works out the shares">Alphastar Review Required</span>
                  }
                  @if (p.description) {
                    <span>{{ p.description }}</span>
                  }
                </div>
              </td>
              @if (showDoNotTrade) {
                <td class="px-15 py-8 text-right">
                  @if (p.isDoNotTrade) {
                    <span class="px-8 py-1 text-orange-dark bg-orange-lightest border border-orange-lighter rounded-full"
                      style="font-size: 1rem">DO NOT TRADE</span>
                  } @else { — }
                </td>
              }
              <td class="px-15 py-8 text-right">{{ p.shares ? (p.shares | number) : '—' }}</td>
              <td class="px-15 py-8 text-right">{{ p.shares ? (p.value / p.shares | currency) : '—' }}</td>
              <td class="px-15 py-8 text-right">{{ p.value | currency }}</td>
            </tr>
          }
          <!-- THE SLEEVE'S SETTLED CASH, in one band with its parts itemised beneath.

               Two things end up here, and they are two different INSTRUCTIONS, which is why they
               are grouped rather than summed into one row:

                 Custodial Cash      the account already held it and the advisor left it alone
                 Set-Aside Cash      deliberately held back on step 4

               ⚠️ SETTLED is what this band means, and it is what decides membership. Both of
               these are cash the sleeve simply HOLDS -- one because nobody touched it, one
               because the advisor asked. Nothing has to happen to either. Money waiting on
               somebody is not cash in this sense however much it looks like it, and lives in the
               CASH ON HOLD band below.

               ⚠️ Pending-review buys were a third member until 2026-09-11, and reviewed sale
               proceeds until 2026-09-15. Both moved down to that band, and `totalSleeveCash`
               stopped counting either -- but ONLY here: the arithmetic everywhere else still
               treats them as cash in the sleeve, because that is what they are.

               ⚠️ "Total Cash", not "Total Set-Aside Cash". Kept custodial cash is not set aside
               -- a set-aside is a thing the advisor asked for, where this is money the account
               already had and nobody touched. The old heading was accurate while the band held
               only the set-aside and became wrong the moment it did not.

               ⚠️ Kept cash was briefly listed up with the securities instead. That was a fix for
               a worse bug -- it had been nested inside the protected-cash `@if`, so it only
               rendered when a buy happened to be pending and otherwise vanished from a table
               whose footer still counted it. Grouping it here keeps that fixed AND answers "how
               much of this sleeve ends up as cash" in one figure.

               Parent first, children indented beneath: indentation reads UPWARD, so the rollup
               has to lead or the children look like they belong to the row above the group.

               The children are NOT also in the list above -- `resultingHoldings` excludes the
               pending rows, and kept cash never went through it. -->
          <!-- ⚠️ Gated on `cashBandRows`, which is never filtered, rather than on `keptCashRows`,
               which drops a row once nothing is left -- so a request releasing a WHOLE cash
               position used to render no cash band at all, erasing the balance from the page. -->
          @if (store.cashBandRows().length > 0 || store.cashInput() > 0) {
            <tr class="border-t border-gray-lightest bg-mint-lightest">
              <td class="px-15 py-8 font-semibold border-l-4 border-mint-light">CASH</td>
              <td class="px-15 py-8 font-semibold text-mint-darkest">Total Cash</td>
              @if (showDoNotTrade) { <td class="px-15 py-8 text-right">&#8212;</td> }
              <!-- No share count on the rollup: the lines it sums do not share a unit, and the
                   pending rows have no shares at all until their ticker is resolved. -->
              <td class="px-15 py-8 text-right text-gray-light">&#8212;</td>
              <td class="px-15 py-8 text-right text-gray-light">&#8212;</td>
              <td class="px-15 py-8 font-semibold text-right">{{ store.totalSleeveCash() | currency }}</td>
            </tr>
            <!-- ⚠️ WHAT THE ACCOUNT HOLDS, not what it keeps -- so the column reconciles. Held
                 minus released is the kept figure, which is what Total Cash above sums, and the
                 three now read as before, change, after. Showing the kept figure here instead
                 left a deduction that subtracted from nothing: value, minus something, equals the
                 same value. -->
            @for (c of store.cashBandRows(); track c.ticker) {
              <tr class="bg-mint-lightest">
                <td class="px-15 py-5 pl-30 text-gray-dark border-l-4 border-mint-light">{{ c.ticker }}</td>
                <td class="px-15 py-5 text-gray-dark">{{ c.description }}</td>
                @if (showDoNotTrade) { <td class="px-15 py-5 text-right">&#8212;</td> }
                <td class="px-15 py-5 text-right text-gray-dark">{{ c.held | number }}</td>
                <td class="px-15 py-5 text-right text-gray-dark">{{ 1 | currency }}</td>
                <td class="px-15 py-5 text-right text-gray-dark">{{ c.held | currency }}</td>
              </tr>
              @if (c.released > 0) {
                <tr class="bg-mint-lightest">
                  <td class="px-15 py-5 pl-30 text-gray border-l-4 border-mint-light">{{ c.ticker }}</td>
                  <td class="px-15 py-5 text-gray">Released toward this request</td>
                  @if (showDoNotTrade) { <td class="px-15 py-5 text-right">&#8212;</td> }
                  <td class="px-15 py-5 text-right text-gray-light">&#8212;</td>
                  <td class="px-15 py-5 text-right text-gray-light">&#8212;</td>
                  <!-- ⚠️ PARENTHESISED, the accounting convention for a deduction. It is the one
                       figure in this column that is subtracted rather than held, and grey alone
                       did not say so -- a lighter $3,481.00 still reads as a balance. -->
                  <td class="px-15 py-5 text-right text-gray">({{ c.released | currency }})</td>
                </tr>
              }
            }
            @if (store.cashInput() > 0) {
              <tr class="bg-mint-lightest">
                <!-- ⚠️ SET-ASIDE CASH, matching the ticker this line is actually filed under --
                     see `buildRequest`. It read CASH, which put the word twice in one group and
                     shared a ticker with the account's own cash row; then PROTECTED CASH, until
                     the set-aside stopped being grouped with the money held for review. Change
                     this only together with the payload and both trackers' ticker lists. -->
                <td class="px-15 py-5 pl-30 text-gray-dark border-l-4 border-mint-light">SET-ASIDE CASH</td>
                <td class="px-15 py-5 text-gray-dark">Cash set aside at the advisor's request</td>
                @if (showDoNotTrade) { <td class="px-15 py-5 text-right">&#8212;</td> }
                <td class="px-15 py-5 text-right text-gray-dark">{{ store.cashInput() | number }}</td>
                <td class="px-15 py-5 text-right text-gray-dark">{{ 1 | currency }}</td>
                <td class="px-15 py-5 text-right text-gray-dark">{{ store.cashInput() | currency }}</td>
              </tr>
            }
          }

          <!-- CASH ON HOLD, its own band BELOW the settled cash. Money this request cannot
               invest because something is UNRESOLVED, whatever left it that way:

                 a BUY  whose ticker the Trade Team has yet to match
                 a SELL outside Mutual Fund / CASH / Equity, whose proceeds cannot be committed

               ⚠️ These rows used to sit INSIDE the cash band -- the buys until 2026-09-11, the
               sells until 2026-09-15. Arithmetically that was right; both do end up as cash in
               the sleeve. It was still wrong to show, because grouping them under cash files
               them as a BALANCE when they are a JOB. Someone has to act before the request can
               be executed, and that is not the same kind of fact as "the account holds $12,000".

               Their own band with their own total says it once, structurally, instead of relying
               on a pill to be noticed inside a group about something else. The pill is gone from
               the rows for the same reason -- the heading now carries it, and repeating it on
               every child was the shape of a warning that had nowhere better to live.

               ⚠️ SAME FIGURE THE BAR DRAWS as Cash On Hold, and the same membership. The two
               surfaces disagreeing about what is on hold is exactly what this re-grouping was
               for, so the total reads `cashOnHold` rather than re-adding the parts here.

               ⚠️ The advisor's own set-aside is NOT here. It is cash the sleeve simply holds,
               by instruction, and nothing has to happen to it -- so it belongs with the settled
               cash above however much it resembles these two.

               ⚠️ ORANGE, not mint. Mint means "the request" everywhere in this wizard, and this
               band is the one part of it that is NOT settled. Same three orange tokens as the
               pill it replaces, so the colour still reads as the same idea.

               ⚠️ BELOW the cash band (AndyP, 2026-09-11). It was put above on the reasoning
               that the only actionable thing in the table should not sit behind the settled
               money. In the table as built that argument does not survive contact: this band is
               the LAST thing before the Total, so it is not buried, and leading with it made an
               exception the first thing read on a request whose cash is the ordinary case. -->
          @if (store.pendingReviewHoldings().length > 0 || store.reviewSoldValue() > 0) {
            <tr class="border-t border-gray-lightest bg-orange-lightest">
              <!-- ⚠️ "PENDING", not "CASH ON HOLD" (AndyP, 2026-09-15). The band sits inside
                   Custom Holdings, whose other band is already CASH, so leading with the word
                   again said the obvious twice and pushed the actual distinction -- that these
                   are waiting on somebody -- to the end of the line. The children drop their own
                   "Pending" for the same reason: the heading carries it, so repeating it made
                   every row a sentence about the group rather than about itself.

                   ⚠️ The BAR still reads Cash On Hold, deliberately. There the run sits beside
                   Custom Holdings and Allocated to Models with no heading above it, so it has to
                   name the whole thing itself. -->
              <td class="px-15 py-8 font-semibold border-l-4 border-orange-lighter text-orange-darker">PENDING</td>
              <td class="px-15 py-8 font-semibold text-orange-darker">Not available to invest in this request</td>
              @if (showDoNotTrade) { <td class="px-15 py-8 text-right">&#8212;</td> }
              <!-- No share count on any of this: a pending row has no shares at all until its
                   ticker is resolved, and the held-proceeds line is a dollar figure, not a lot. -->
              <td class="px-15 py-8 text-right text-gray-light">&#8212;</td>
              <td class="px-15 py-8 text-right text-gray-light">&#8212;</td>
              <td class="px-15 py-8 font-semibold text-right text-orange-darker">{{ store.cashOnHold() | currency }}</td>
            </tr>
            <!-- TWO ROLLED-UP LINES, one per direction, and neither itemised per security.
                 Both sides earn the rollup the same way: the per-security detail already exists
                 in the tables above -- each sell carries its own Review Required flag, and each
                 pending buy is now listed in Positions Being Bought with one too -- so itemising
                 here would print the same securities twice on one page and leave the advisor
                 working out whether that was two holds or one.

                 What this band answers is "how much is held, and why", which is two numbers.

                 ⚠️ PENDING SELLS is a real ticker, matching what `buildRequest` files. PENDING
                 BUYS is NOT -- those rows file individually under the tickers the advisor typed,
                 and this is a label over their sum. Do not add it to either tracker's cash-ticker
                 list: nothing is ever filed under it. -->
            @if (store.protectedCashFromReview() > 0) {
              <tr class="bg-orange-lightest">
                <td class="px-15 py-5 pl-30 border-l-4 border-orange-lighter text-orange-darker">Buys</td>
                <td class="px-15 py-5 text-orange-darker">Cash hold placed for purchase of requested alternative securities.</td>
                @if (showDoNotTrade) { <td class="px-15 py-5 text-right">&#8212;</td> }
                <td class="px-15 py-5 text-right text-gray-light">&#8212;</td>
                <td class="px-15 py-5 text-right text-gray-light">&#8212;</td>
                <td class="px-15 py-5 text-right text-orange-darker">{{ store.protectedCashFromReview() | currency }}</td>
              </tr>
            }
            @if (store.reviewSoldValue() > 0) {
              <tr class="bg-orange-lightest">
                <td class="px-15 py-5 pl-30 border-l-4 border-orange-lighter text-orange-darker">Sells</td>
                <td class="px-15 py-5 text-orange-darker">Estimated liquidated proceeds from alternative securities being sold.</td>
                @if (showDoNotTrade) { <td class="px-15 py-5 text-right">&#8212;</td> }
                <td class="px-15 py-5 text-right text-gray-light">&#8212;</td>
                <td class="px-15 py-5 text-right text-gray-light">&#8212;</td>
                <td class="px-15 py-5 text-right text-orange-darker">{{ store.reviewSoldValue() | currency }}</td>
              </tr>
            }
          }
          <!-- ⚠️ `resultingHoldings()` covers the pending rows too -- they are in it, flagged --
               so this stays correct now that they render in their own band rather than the cash
               one. It is the securities list above that filters them out, not this guard. -->
          @if (store.resultingHoldings().length === 0 && store.cashInput() === 0
            && store.keptCashRows().length === 0) {
            <tr><td [attr.colspan]="showDoNotTrade ? 6 : 5" class="italic text-center px-15 py-10 text-gray">No remaining positions.</td></tr>
          }
        </tbody>
        <tfoot>
          <tr class="border-t border-gray-lighter font-semibold text-gray-darkest" style="background-color: #fafafa">
            <td class="px-15 py-4" [attr.colspan]="showDoNotTrade ? 5 : 4">Total</td>
            <td class="px-15 py-4 text-right">{{ store.customHoldingsValue() | currency }}</td>
          </tr>
        </tfoot>
      </table>
    </section>
  }

  <!-- Request notes, shown only when the advisor left one. -->
  @if (store.requestNotesForPayload()) {
    <section class="border rounded border-gray-lighter">
      <header class="px-15 py-8 text-sm font-bold tracking-wide uppercase border-b bg-gray-lightest text-gray-darkest border-gray-lighter">
        Request Notes
      </header>
      <p class="px-15 py-10 text-sm whitespace-pre-wrap text-gray-dark">{{ store.requestNotesForPayload() }}</p>
    </section>
  }

  <!-- Attached files. Only rendered when something was attached, so the review reads
       the same as before for the common no-attachment case. -->
  @if (store.files().length) {
    <section class="border rounded border-gray-lighter">
      <header class="px-15 py-8 text-sm font-bold tracking-wide uppercase border-b bg-gray-lightest text-gray-darkest border-gray-lighter">
        Attached Files ({{ store.files().length }})
      </header>
      <table class="w-full text-sm border-collapse table-fixed">
        <colgroup>
          <col style="width: 84%" />
          <col style="width: 16%" />
        </colgroup>
        <tbody>
          @for (file of store.files(); track file.name + file.size) {
            <tr class="border-t border-gray-lightest">
              <td class="px-15 py-8 font-medium truncate" [title]="file.name">{{ file.name }}</td>
              <td class="px-15 py-8 text-right text-gray-dark">{{ store.formatFileSize(file.size) }}</td>
            </tr>
          }
        </tbody>
      </table>
    </section>
  }
</div>
